import { createSlice } from '@reduxjs/toolkit';
import { logout } from 'redux/slices/userSlice';

// Redes que el embajador tiene que reconectar porque su token venció o fue
// revocado. Las calcula el backend (GET verify-token-social-network/all) y
// `userId` indica para qué embajador se consultaron, así un cambio de sesión
// vuelve a consultar. El slice no se persiste (ver `blacklist` en store.ts):
// al volver del OAuth la página recarga y la lista se recalcula.
export interface InvalidTokenState {
  userId: number | null;
  invalidNetworks: string[];
  dismissed: string[];
}

const initialState: InvalidTokenState = {
  userId: null,
  invalidNetworks: [],
  dismissed: []
};

// Suma lo que ya se agregó en esta sesión (p. ej. un Participar que falló
// mientras la consulta estaba en vuelo), salvo que la lista fuera de otro
// embajador.
const setNetworks = (
  state: InvalidTokenState,
  action: { payload: { userId: number; networks: string[] } }
) => {
  const { userId, networks } = action.payload;
  const sameSession = state.userId === null || state.userId === userId;
  const sessionNetworks = sameSession ? state.invalidNetworks : [];

  return {
    userId,
    invalidNetworks: Array.from(new Set([...sessionNetworks, ...networks])),
    dismissed: sameSession ? state.dismissed : []
  };
};

// Idempotente: lo despachan tarjetas que se re-renderizan, y devolver el mismo
// estado evita re-renders en cadena.
const addNetwork = (state: InvalidTokenState, action: { payload: string }) => {
  if (!action.payload || state.invalidNetworks.includes(action.payload)) {
    return state;
  }

  return {
    ...state,
    invalidNetworks: [...state.invalidNetworks, action.payload],
    dismissed: state.dismissed.filter(network => network !== action.payload)
  };
};

const removeNetwork = (
  state: InvalidTokenState,
  action: { payload: string }
) => {
  if (!state.invalidNetworks.includes(action.payload)) {
    return state;
  }

  return {
    ...state,
    invalidNetworks: state.invalidNetworks.filter(
      network => network !== action.payload
    )
  };
};

const dismissNetwork = (
  state: InvalidTokenState,
  action: { payload: string }
) => {
  if (state.dismissed.includes(action.payload)) {
    return state;
  }

  return {
    ...state,
    dismissed: [...state.dismissed, action.payload]
  };
};

const InvalidTokenSlice = createSlice({
  name: 'invalidToken',
  initialState,
  reducers: {
    setInvalidNetworks: setNetworks,
    addInvalidNetwork: addNetwork,
    removeInvalidNetwork: removeNetwork,
    dismissInvalidNetwork: dismissNetwork
  },
  extraReducers: builder => {
    builder.addCase(logout, () => initialState);
  }
});

export const selectInvalidToken = (state: {
  invalidToken: InvalidTokenState;
}) => state.invalidToken;

export const {
  setInvalidNetworks,
  addInvalidNetwork,
  removeInvalidNetwork,
  dismissInvalidNetwork
} = InvalidTokenSlice.actions;

export default InvalidTokenSlice.reducer;
